/**
 * /flights — Flight Listing (Step 1 of the booking flow).
 *
 * Displays promotional / featured flights. Each card shows Airline, From, To, starting
 * price, and departure date. "Book Now" pre-fills the route into BookingContext and
 * navigates to /bookingflight to start a search.
 *
 * Data comes exclusively from the mock API (airlines + airports), so it demonstrates the
 * loading / empty / error states required by the spec.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAirlines, getAirports } from "../../mock/api";
import { LoadingSpinner, ErrorState, EmptyState } from "../../components/booking/states/StateViews";
import { useBooking } from "../../context/BookingContext";
import "./flight-listing.css";

/** Predefined promotional route pairs (by IATA code) to feature. */
const PROMO_ROUTES = [
  { from: "DXB", to: "JED", date: "2026-08-05", from_base: 48990 },
  { from: "KHI", to: "DXB", date: "2026-08-08", from_base: 41500 },
  { from: "LHE", to: "JED", date: "2026-08-10", from_base: 62200 },
  { from: "ISB", to: "DOH", date: "2026-08-12", from_base: 54800 },
  { from: "DXB", to: "IST", date: "2026-08-15", from_base: 73900 },
  { from: "KHI", to: "RUH", date: "2026-08-18", from_base: 45300 },
];

const CURRENCY = "PKR";

const formatPrice = (amount) =>
  `${CURRENCY} ${amount.toLocaleString("en-PK")}`;

const formatDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export default function FlightListing() {
  const navigate = useNavigate();
  const { setSearchCriteria } = useBooking();

  const [status, setStatus] = useState("loading"); // loading | error | ready
  const [featured, setFeatured] = useState([]);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const [airlines, airports] = await Promise.all([getAirlines(), getAirports()]);
      const byCode = Object.fromEntries(airports.map((a) => [a.airportCode, a]));

      // Build featured cards by pairing promo routes with rotating airlines.
      const cards = PROMO_ROUTES.map((route, i) => {
        const origin = byCode[route.from];
        const destination = byCode[route.to];
        if (!origin || !destination) return null;
        const airline = airlines[i % airlines.length];
        return {
          id: `promo-${route.from}-${route.to}`,
          airline,
          origin,
          destination,
          date: route.date,
          startingPrice: route.from_base,
        };
      }).filter(Boolean);

      setFeatured(cards);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleBookNow = (card) => {
    // Pre-fill the search so the (future) search form opens with this route.
    setSearchCriteria({
      tripType: "oneway",
      from: card.origin.airportCode,
      to: card.destination.airportCode,
      departureDate: card.date,
      travellers: 1,
      adults: 1,
      children: 0,
      infants: 0,
      cabin: "Economy",
      directOnly: false,
    });
    navigate("/bookingflight");
  };

  return (
    <section className="fl-page">
      <header className="fl-hero">
        <div className="fl-hero__inner">
          <span className="fl-hero__eyebrow">Falcon Destinations</span>
          <h1 className="fl-hero__title">Featured Flight Deals</h1>
          <p className="fl-hero__subtitle">
            Handpicked promotional fares across the Gulf, South Asia, and beyond. Pick a
            deal to start your booking.
          </p>
        </div>
      </header>

      <div className="fl-content">
        {status === "loading" && <LoadingSpinner label="Loading featured deals…" />}

        {status === "error" && (
          <ErrorState
            title="Couldn't load deals"
            message="We couldn't reach the flight service. Please try again."
            onRetry={load}
          />
        )}

        {status === "ready" && featured.length === 0 && (
          <EmptyState
            icon="fas fa-plane-slash"
            title="No featured deals right now"
            message="Check back soon for new promotional fares."
          />
        )}

        {status === "ready" && featured.length > 0 && (
          <div className="fl-grid">
            {featured.map((card) => (
              <article key={card.id} className="fl-card">
                <div className="fl-card__airline">
                  <img
                    src={card.airline.logo}
                    alt={card.airline.name}
                    className="fl-card__logo"
                    width={40}
                    height={40}
                  />
                  <span className="fl-card__airline-name">{card.airline.name}</span>
                </div>

                <div className="fl-card__route">
                  <div className="fl-card__endpoint">
                    <span className="fl-card__code">{card.origin.airportCode}</span>
                    <span className="fl-card__city">{card.origin.city}</span>
                  </div>
                  <div className="fl-card__route-line" aria-hidden="true">
                    <i className="fas fa-plane" />
                  </div>
                  <div className="fl-card__endpoint fl-card__endpoint--right">
                    <span className="fl-card__code">{card.destination.airportCode}</span>
                    <span className="fl-card__city">{card.destination.city}</span>
                  </div>
                </div>

                <div className="fl-card__meta">
                  <span className="fl-card__date">
                    <i className="fas fa-calendar-day" aria-hidden="true" />{" "}
                    {formatDate(card.date)}
                  </span>
                </div>

                <div className="fl-card__footer">
                  <div className="fl-card__price">
                    <span className="fl-card__price-label">Starting from</span>
                    <span className="fl-card__price-value">
                      {formatPrice(card.startingPrice)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="fl-card__book"
                    onClick={() => handleBookNow(card)}
                  >
                    Book Now
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
